import Image from 'next/image'
import { useState } from 'react'

import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { ModalName } from '@/components/entities/requests/ui/RequestDetails/Content/RequestRating/types/types'
import { AppButton } from '@/components/shared/button'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

import { AddRatingModal } from './AddRatingModal'
import { FeedbackCompletedModal } from './FeedbackCompletedModal'
import { ProblemNotSolvedModal } from './ProblemNotSolvedModal'
import { RequestRatingMobileModal } from './RequestRatingMobileModal'

type Props = {
  canBeRated: boolean
  requestType: RequestTypeAlias
  id: string
  isButtonUnsolvedAvailable: boolean
  refetchRequest: () => void
}

const ContentBlockRequestRating = ({
  canBeRated,
  requestType,
  id,
  isButtonUnsolvedAvailable,
  refetchRequest
}: Props) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)

  const { reachGoal } = useYandexMetrika()
  const [modalName, setModalName] = useState<ModalName | null>(null)

  const areModalsMounted =
    canBeRated || modalName === ModalName.FeedbackCompleted

  const onRateButtonClick = () => {
    setModalName(ModalName.AddRating)
    reachGoal(
      requestType === RequestTypeAlias.CommonRequest
        ? YandexMetrikaEvent.NEW_REQUEST_FEEDBACK_BUTTON
        : YandexMetrikaEvent.PASSES_FEEDBACK_BUTTON
    )
  }

  return (
    <>
      {canBeRated && (
        <AppButton
          className="sm:mb-16"
          variant="secondary"
          onClick={onRateButtonClick}
        >
          <div className="flex items-center gap-8">
            <Image
              src="/images/requests/star.svg"
              alt="arrow-right-btn"
              width={14}
              height={11}
            />
            <span>Оценить</span>
          </div>
        </AppButton>
      )}
      {areModalsMounted && !isMobileScreen && (
        <>
          <AddRatingModal
            id={id}
            isButtonUnsolvedAvailable={isButtonUnsolvedAvailable}
            visible={modalName === ModalName.AddRating}
            onClose={() => setModalName(null)}
            setModalName={setModalName}
            refetchRequest={refetchRequest}
          />
          <ProblemNotSolvedModal
            id={id}
            visible={modalName === ModalName.ProblemNotSolved}
            onClose={() => setModalName(null)}
            setModalName={setModalName}
            refetchRequest={refetchRequest}
          />
          <FeedbackCompletedModal
            visible={modalName === ModalName.FeedbackCompleted}
            onClose={() => setModalName(null)}
          />
        </>
      )}

      {areModalsMounted && isMobileScreen && (
        <RequestRatingMobileModal
          id={id}
          isButtonUnsolvedAvailable={isButtonUnsolvedAvailable}
          modalName={modalName}
          onClose={() => setModalName(null)}
          setModalName={setModalName}
          refetchRequest={refetchRequest}
        />
      )}
    </>
  )
}

export { ContentBlockRequestRating }
